import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';
import { Card, SelectHit, Content, Title, Desc, MoreInline } from './NameCard.styles';

export interface NameCardProps {
  name: string;
  /** Rationale shown under the title (from the API). */
  description?: string;
  selected: boolean;
  onSelect: () => void;
  /** When provided, a long rationale is trimmed to a short preview and an inline
   *  "More" reveals the full name + text (desktop). Omit for the full card. */
  onMore?: () => void;
}

/** Roughly two lines on the desktop cards; longer rationales trim to this and
 *  hand off to the "More" reveal. */
const PREVIEW_LIMIT = 90;

/** Trim to the last whole word within the limit and append an ellipsis. */
const buildPreview = (text: string): string => {
  const cut = text.slice(0, PREVIEW_LIMIT);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

/** A single AI name suggestion card (title · rationale [· inline "More"]). */
export const NameCard = ({ name, description, selected, onSelect, onMore }: NameCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const truncated = Boolean(onMore && description && description.length > PREVIEW_LIMIT);
  const preview = description ? (truncated ? buildPreview(description) : description) : '';
  return (
    <Card $selected={selected}>
      <SelectHit type="button" aria-label={t('Select {{name}}', { name })} onClick={onSelect} />
      <Content>
        <Title text={name} tag="span" $selected={selected} />
        {description ? (
          <Desc>
            <Text text={preview} tag="span" />
            {truncated ? (
              <MoreInline type="button" onClick={onMore}>
                <Text text="More" tag="span" />
              </MoreInline>
            ) : null}
          </Desc>
        ) : null}
      </Content>
    </Card>
  );
};

export default NameCard;
